import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDeckModal } from '../../contexts/DeckModalContext';
import { collection, getDocs, getDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase.config';
import '../../styles/kyro-design.css';
import './Home.css';

const AMAN_PHOTO_FALLBACK = '/assets/team/aman-maheshwari.jpeg';


const fundManagerExpertise = [
  { title: '10+ Years in Asian Markets', points: ['Equity markets strategy, portfolio management & asset allocation across Asia', 'Deep expertise in cross-border capital flows & market cycles'] },
  { title: 'IPO Capital Markets Consultation', points: ['Advised growth-stage companies on capital structuring, investor readiness, selection of intermediaries and roadshow participation ahead of public markets', 'Pre-IPO fundraises with marquee institutional investors across Asia'] },
  { title: 'AIF Strategy', points: ['Strategy: capital preservation + asymmetric alpha generation', 'Institutional-grade research, risk frameworks & IC governance'] },
];

const COMPANY_LOGO_MAP: Record<string, string> = {
  'Goldman Sachs': '/assets/companies/goldman.jpg',
  'JPMorgan Chase': '/assets/companies/jpmorgan.jpg',
  'NOMURA': '/assets/companies/nomura.jpg',
};

interface FundManagerData {
  name: string;
  role: string;
  photoURL: string;
  education?: string[];
  previouslyAt?: string[];
}

const DEFAULT_STATS = { targetIRR: '35%', minInvestment: '₹1 Cr', transactionsAdvised: '₹550 Cr+', yearsAsianMarkets: '10+' };

const Home: React.FC = () => {
  const { openDeckModal } = useDeckModal();
  const [fundManager, setFundManager] = useState<FundManagerData | null>(null);
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const homeStatsDoc = await getDoc(doc(db, 'siteConfig', 'homeStats'));
        const hs = homeStatsDoc.data();
        if (hs) setStats({ targetIRR: hs.targetIRR || DEFAULT_STATS.targetIRR, minInvestment: hs.minInvestment || DEFAULT_STATS.minInvestment, transactionsAdvised: hs.transactionsAdvised || DEFAULT_STATS.transactionsAdvised, yearsAsianMarkets: hs.yearsAsianMarkets || DEFAULT_STATS.yearsAsianMarkets });
      } catch { /* use defaults */ }
    };
    loadStats();
  }, []);

  useEffect(() => {
    const loadFundManager = async () => {
      try {
        const q = query(collection(db, 'teamMembers'), orderBy('order'));
        const snap = await getDocs(q);
        const visible = snap.docs.filter((d) => d.data().showOnWebsite !== false);
        const first =
          visible.find((d) => (d.data().name || '').toLowerCase().includes('aman')) || visible[0];
        const data = first?.data();
        if (data) {
          setFundManager({
            name: data.name || 'Aman Maheshwari',
            role: data.role || 'Founder & Fund Manager · Investment Committee Member',
            photoURL: data.photoURL || AMAN_PHOTO_FALLBACK,
            education: data.education || ['MBA Finance - IBS Hyderabad', 'NISM XIX-C: AIF Certified'],
            previouslyAt: data.previouslyAt || ['Goldman Sachs', 'JPMorgan Chase', 'NOMURA'],
          });
        } else {
          setFundManager({ name: 'Aman Maheshwari', role: 'Founder & Fund Manager · Investment Committee Member', photoURL: AMAN_PHOTO_FALLBACK, education: ['MBA Finance - IBS Hyderabad', 'NISM XIX-C: AIF Certified'], previouslyAt: ['Goldman Sachs', 'JPMorgan Chase', 'NOMURA'] });
        }
      } catch {
        setFundManager({ name: 'Aman Maheshwari', role: 'Founder & Fund Manager · Investment Committee Member', photoURL: AMAN_PHOTO_FALLBACK, education: ['MBA Finance - IBS Hyderabad', 'NISM XIX-C: AIF Certified'], previouslyAt: ['Goldman Sachs', 'JPMorgan Chase', 'NOMURA'] });
      }
    };
    loadFundManager();
  }, []);

  return (
    <div className="page">
      <div className="hero">
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner">
          <h1>
            Institutional-Grade<br />
            Alternative Investments.<br />
            <span className="dim">Structured Alpha. Long-Term Capital.</span>
          </h1>
          <p className="hero-sub">
            Delivering disciplined access to India's most compelling growth-stage and listed opportunities — for sophisticated investors who think in decades, not quarters.
          </p>
          <div className="hero-btns">
            <button type="button" onClick={openDeckModal} className="btn-p">Request Fund Deck</button>
            <Link to="/contact" className="btn-g">Connect with Investment Team</Link>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="scroll-line" />
          SCROLL
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="stat-v">{stats.targetIRR}</div>
          <div className="stat-l">Target IRR</div>
        </div>
        <div className="stat">
          <div className="stat-v">{stats.minInvestment}</div>
          <div className="stat-l">Min. Investment</div>
        </div>
        <div className="stat">
          <div className="stat-v">₹100 Cr</div>
          <div className="stat-l">Target Corpus</div>
        </div>
        <div className="stat">
          <div className="stat-v">{stats.yearsAsianMarkets}</div>
          <div className="stat-l">Years Asian Markets</div>
        </div>
      </div>

      <div className="sec">
        <div className="grid-2">
          <div>
            <div className="eyebrow">Who We Are</div>
            <div className="sec-title">
              Enduring Capital.<br />Institutional Discipline.
            </div>
            <p className="sec-body" style={{ marginBottom: '1rem' }}>
              Kyro Asset Management is a research-driven investment management firm focused on delivering long-term capital appreciation through a disciplined institutional approach. We act as Investment Manager to our SEBI-registered Category II AIF (Reg. No. IN/AIF2/26-27/2191).
            </p>
            <p className="sec-body" style={{ marginBottom: '1.4rem' }}>
              With deep expertise in Indian financial markets, we cater to HNIs, UHNIs, Family Offices and Institutional Investors seeking differentiated access to growth-oriented opportunities across private and public markets.
            </p>
            <Link to="/about" className="btn-p" style={{ fontSize: '0.78rem', padding: '0.65rem 1.3rem' }}>
              Learn More About Us
            </Link>
          </div>
          <div className="pillars">
            {[
              { n: '01', t: 'Strategic Partner', d: 'Proprietary access to deal flow from our Kyro Capital network — including our investment banking franchise — enabling unique sourcing opportunities ahead of formal processes.' },
              { n: '02', t: 'Differentiated Insights', d: 'Thematic investing approach supported by expertise across sectors, markets, business models and stages of corporate lifecycle — identifying disruptive forces early.' },
              { n: '03', t: 'Institutional Framework', d: 'Research-led decisions, rigorous risk management, structured IC governance and independent valuation — built to institutional standards from Day 1.' },
            ].map((p) => (
              <div className="pillar" key={p.n}>
                <div className="pillar-num">{p.n}</div>
                <div>
                  <div className="pillar-title">{p.t}</div>
                  <div className="pillar-desc">{p.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec-navy">
        <div className="grid-2" style={{ alignItems: 'center' }}>
          <div>
            <div className="eyebrow eyebrow-lt">Flagship Fund</div>
            <div className="sec-title sec-title-lt">Kyro India Opportunities Fund – I</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.42)', letterSpacing: '0.01em', lineHeight: 1.5, marginTop: '-0.35rem', marginBottom: '1rem' }}>
              (scheme of Kyro Alternative Investment Trust – Category-II AIF) · SEBI Reg. No. IN/AIF2/26-27/2191
            </div>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.48)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              Our SEBI-registered Category II AIF designed to generate long-term capital appreciation through high-conviction investments, structured governance and rigorous risk management — deployed across growth-stage and listed market opportunities.
            </p>
            <Link to="/asset-management" className="btn-p">Explore the Fund</Link>
          </div>
          <div className="aif-hl-grid">
            {[
              { v: 'Category II', l: 'AIF Classification' },
              { v: '35%', l: 'Target IRR' },
              { v: '₹1 Crore', l: 'Min. Investment' },
              { v: '5 + 2 Yrs', l: 'Fund Tenure' },
            ].map((h) => (
              <div className="aif-hl" key={h.l}>
                <div className="aif-hl-v">{h.v}</div>
                <div className="aif-hl-l">{h.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec fund-manager-sec">
        <div className="eyebrow" style={{ color: 'var(--blue)' }}>FUND MANAGER</div>
        <div className="sec-title" style={{ marginBottom: '2rem', paddingBottom: '0.75rem', borderBottom: '3px solid var(--blue)' }}>
          Leadership with Institutional Pedigree
        </div>
        <div className="fund-manager-grid">
          <div className="fund-manager-left">
            <div className="fund-manager-photo-wrap">
              <img src={fundManager?.photoURL || AMAN_PHOTO_FALLBACK} alt={fundManager?.name || 'Fund Manager'} className="fund-manager-photo" />
            </div>
            <div className="fund-manager-name-bar">
              <div className="fund-manager-name">{fundManager?.name || 'Aman Maheshwari'}</div>
              <div className="fund-manager-titles">{fundManager?.role || 'Founder & Fund Manager · Investment Committee Member'}</div>
            </div>
          </div>
          <div className="fund-manager-right">
            {fundManager?.previouslyAt && fundManager.previouslyAt.length > 0 && (
              <div className="fm-previously">
                <div className="fm-label">PREVIOUSLY AT</div>
                <div className="fm-company-boxes">
                  {fundManager.previouslyAt.map((name) => {
                    const logo = COMPANY_LOGO_MAP[name];
                    return logo ? (
                      <div key={name} className="fm-company">
                        <img src={logo} alt={name} className="fm-company-logo" />
                      </div>
                    ) : (
                      <div key={name} className="fm-company fm-company-text">{name}</div>
                    );
                  })}
                </div>
              </div>
            )}
            {fundManager?.education && fundManager.education.length > 0 && (
              <div className="fm-edu">
                {fundManager.education.map((e) => (
                  <div key={e} className="fm-edu-box">{e}</div>
                ))}
              </div>
            )}
            <div className="fm-expertise">
              {fundManagerExpertise.map(({ title, points }) => (
                <div key={title} className="fm-expertise-col">
                  <div className="fm-expertise-title">{title}</div>
                  <ul className="fm-expertise-list">
                    {points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="fm-career-track">
              CAREER TRACK &gt; Goldman Sachs | JPMorgan Chase | Nomura | Kyro Capital
            </div>
          </div>
        </div>
      </div>

      <div className="sec-navy" style={{ textAlign: 'center' }}>
        <div style={{ position: 'relative', maxWidth: 560, margin: '0 auto' }}>
          <div className="eyebrow eyebrow-lt">Start Your Journey</div>
          <div className="sec-title sec-title-lt" style={{ marginBottom: '0.8rem' }}>
            Ready to Invest with Discipline?
          </div>
          <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.44)', lineHeight: 1.8, marginBottom: '2rem' }}>
            Whether you are an HNI, family office or institution — we invite you to explore Kyro India Opportunities Fund – I
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={openDeckModal} className="btn-p">Request Fund Deck</button>
            <Link to="/contact" className="btn-g">Schedule Consultation</Link>
          </div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.34)', lineHeight: 1.6, marginTop: '1.6rem' }}>
            Kyro India Opportunities Fund – I (scheme of Kyro Alternative Investment Trust – Category-II AIF) · SEBI Registration No. IN/AIF2/26-27/2191
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
